import React from 'react';
import { marked } from 'marked';
import Linkify from 'linkify-react';
import DOMPurify from 'dompurify';
import { Trash2 } from 'react-feather';
import styles from './Message.module.css';

// CONFIGURE marked for code blocks (AC4.9)
marked.setOptions({
  breaks: true,
  gfm: true
});

function Message({ message, currentUser, users = [], onDelete }) {
  // FORMAT timestamp [HH:MM]
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `[${hours}:${minutes}]`;
  };

  // HANDLE message deletion (Story 10: AC10.2)
  const handleDelete = () => {
    if (window.confirm('Delete this message?')) {
      onDelete(message.id);
    }
  };

  // GENERATE consistent color for user (AC4.7)
  const getUserColor = (handle) => {
    const colors = ['#1e5aa8', '#e82a1f', '#276738', '#f97316', '#9333ea', '#0891b2'];
    const hash = handle.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  // CHECK if username is valid (Story 8: AC8.5)
  const isValidUser = (username) => {
    return users.some(u => u.handle.toLowerCase() === username.toLowerCase());
  };

  // DETECT mentions in text (Story 8: AC8.1, AC8.4)
  const parseWithMentions = (text) => {
    const mentionRegex = /@(\w+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      const username = match[1];

      // ADD text before mention
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.substring(lastIndex, match.index)
        });
      }

      // ADD mention (Story 8: AC8.2, AC8.5)
      parts.push({
        type: 'mention',
        content: `@${username}`,
        username: username,
        isValid: isValidUser(username)
      });

      lastIndex = match.index + match[0].length;
    }

    // ADD remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex)
      });
    }

    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  // CHECK if current user is mentioned (Story 8: AC8.3, AC8.6)
  const isCurrentUserMentioned = (text) => {
    if (!currentUser) return false;
    const mentionRegex = /@(\w+)/g;
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      if (match[1].toLowerCase() === currentUser.handle.toLowerCase()) {
        return true;
      }
    }

    return false;
  };

  // SYSTEM message (AC4.6)
  if (message.message_type === 'system') {
    const systemContent = message.content || message.message;
    return (
      <div className={styles.systemMessage}>
        <span className={styles.timestamp}>{formatTime(message.created_at)}</span>
        <span className={styles.systemText}>
          {systemContent.split('\n').map((line, i) => (
            <React.Fragment key={i}>
              {line}
              {i < systemContent.split('\n').length - 1 && <br />}
            </React.Fragment>
          ))}
        </span>
      </div>
    );
  }

  // ACTION message (AC4.5) - /me command (Story 5: AC5.4)
  // Also handle deleted action messages (when message was originally action type)
  if (message.message_type === 'action' || (message.message_type === 'deleted' && message.handle)) {
    const actionContent = message.message || message.content;
    const handle = message.handle || message.user?.handle || 'System';
    return (
      <div className={styles.actionMessage}>
        <span className={styles.timestamp}>{formatTime(message.created_at)}</span>
        <span className={styles.actionText}>
          * {handle} {actionContent}
        </span>
        {/* Story 10: AC10.2, AC10.4 - Admin can delete action messages too */}
        {currentUser?.is_admin && onDelete && message.message_type !== 'deleted' && (
          <button
            className={styles.deleteButton}
            onClick={handleDelete}
            title="Delete message"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
    );
  }

  // WHISPER message (Story 6: AC6.2, AC6.3)
  if (message.direction) {
    const whisperTimestamp = message.timestamp || message.created_at;
    return (
      <div className={styles.whisperMessage}>
        <span className={styles.timestamp}>{formatTime(whisperTimestamp)}</span>
        <span className={styles.whisperText}>
          [whisper {message.direction} {message.otherUser}] {message.message}
        </span>
      </div>
    );
  }

  // REGULAR message with rich content
  const userColor = message.user ? getUserColor(message.user.handle) : '#6b7280';
  const messageText = message.message || message.content;

  // CHECK if current user is mentioned (Story 8: AC8.3)
  const mentioned = isCurrentUserMentioned(messageText);

  // PARSE message for code blocks and markdown (AC4.9)
  const hasCodeBlock = messageText.includes('```');

  let messageContent;
  if (hasCodeBlock) {
    // EXTRACT language from code block for badge
    const codeBlockMatch = messageText.match(/```(\w+)?\n/);
    const language = codeBlockMatch && codeBlockMatch[1] ? codeBlockMatch[1] : null;

    // RENDER markdown with code blocks
    let htmlContent = marked.parse(messageText);

    // INJECT language badge into the pre tag if language exists
    if (language) {
      htmlContent = htmlContent.replace(
        /<pre>/,
        `<pre><div class="${styles.codeBadge}">${language}</div>`
      );
    }

    const sanitizedHtml = DOMPurify.sanitize(htmlContent);

    messageContent = (
      <span
        className={styles.content}
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    );
  } else {
    // PARSE for mentions first (Story 8: AC8.1, AC8.2)
    const parts = parseWithMentions(messageText);

    // LINKIFY URLs (AC4.10)
    const linkifyOptions = {
      target: '_blank',
      rel: 'noopener noreferrer',
      className: styles.link
    };

    messageContent = (
      <span className={styles.content}>
        {parts.map((part, index) => {
          if (part.type === 'mention' && part.isValid) {
            // VALID mention - highlight it (Story 8: AC8.2)
            return (
              <span key={index} className={styles.mention}>
                {part.content}
              </span>
            );
          } else {
            // REGULAR text or invalid mention
            return (
              <Linkify key={index} options={linkifyOptions}>
                {part.content}
              </Linkify>
            );
          }
        })}
      </span>
    );
  }

  return (
    <div className={`${styles.message} ${mentioned ? styles.mentioned : ''}`}>
      <span className={styles.timestamp}>{formatTime(message.created_at)}</span>
      <span className={styles.handle} style={{ color: userColor }}>
        &lt;{message.user?.handle || 'Unknown'}&gt;
      </span>
      {messageContent}
      {/* Story 10: AC10.2, AC10.4 - Admin delete button */}
      {currentUser?.is_admin && onDelete && message.message_type !== 'deleted' && (
        <button
          className={styles.deleteButton}
          onClick={handleDelete}
          title="Delete message"
        >
          <Trash2 size={16} />
        </button>
      )}
    </div>
  );
}

export default Message;
